import React, { CSSProperties, useCallback, useRef } from "react"

export interface ResizablePanelProps {
  className?: string
  style?: CSSProperties
  resizeHandlePosition?: "right" | "bottom" | "both"
  onResizeStart?: (e: React.MouseEvent) => void
  onResizeEnd?: () => void
  children: React.ReactNode
  resizeHandleClassName?: string
  verticalResizeHandleClassName?: string
  onResize?: (dimensions: { width?: string; height?: string }) => void
}

const ResizablePanel: React.FC<ResizablePanelProps> = ({
  className = "",
  style = {},
  resizeHandlePosition = "right",
  onResizeStart,
  onResizeEnd,
  children,
  resizeHandleClassName = "absolute w-2 h-full cursor-ew-resize right-0 top-0 z-10 bg-blue-500 opacity-0 hover:opacity-50",
  verticalResizeHandleClassName = "absolute h-2 w-full cursor-ns-resize bottom-0 left-0 z-10 bg-blue-500 opacity-0 hover:opacity-50",
  onResize,
}) => {
  const startPositionRef = useRef({ x: 0, y: 0 })
  const panelRef = useRef<HTMLDivElement>(null)

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      if (!panelRef.current || !onResize) return

      e.preventDefault()

      const isHorizontal = resizeHandlePosition === "right"
      const startPosition = isHorizontal ? e.clientX : e.clientY
      const panel = panelRef.current
      const rect = panel.getBoundingClientRect()

      // Get parent container dimensions
      const parentRect = panel.parentElement?.getBoundingClientRect()
      if (!parentRect) return

      const parentWidth = parentRect.width
      const parentHeight = parentRect.height

      // Initial sizes
      const startWidth = rect.width
      const startHeight = rect.height

      // Calculate initial percentages
      const initialWidthPercent = (startWidth / parentWidth) * 100
      const initialHeightPercent = (startHeight / parentHeight) * 100

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!panel.parentElement) return

        const currentPosition = isHorizontal
          ? moveEvent.clientX
          : moveEvent.clientY
        const delta = currentPosition - startPosition

        if (isHorizontal) {
          // Horizontal resizing (width)
          const newWidth = startWidth + delta
          const newWidthPercent = (newWidth / parentWidth) * 100

          // Limit resize to reasonable bounds (10% to 90% of parent)
          const clampedWidthPercent = Math.max(
            10,
            Math.min(90, newWidthPercent),
          )
          panel.style.width = `${clampedWidthPercent}%`

          if (onResize) {
            onResize({ width: `${clampedWidthPercent}%` })
          }
        } else {
          // Vertical resizing (height)
          const newHeight = startHeight + delta
          const newHeightPercent = (newHeight / parentHeight) * 100

          // Limit resize to reasonable bounds (10% to 90% of parent)
          const clampedHeightPercent = Math.max(
            10,
            Math.min(90, newHeightPercent),
          )
          panel.style.height = `${clampedHeightPercent}%`

          if (onResize) {
            onResize({ height: `${clampedHeightPercent}%` })
          }
        }
      }

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
        if (onResizeEnd) onResizeEnd()
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    },
    [resizeHandlePosition, onResize],
  )

  return (
    <div ref={panelRef} className={`relative ${className}`} style={style}>
      {children}

      {(resizeHandlePosition === "right" ||
        resizeHandlePosition === "both") && (
        <div className={resizeHandleClassName} onMouseDown={startResize} />
      )}

      {(resizeHandlePosition === "bottom" ||
        resizeHandlePosition === "both") && (
        <div
          className={verticalResizeHandleClassName}
          onMouseDown={startResize}
        />
      )}
    </div>
  )
}

export default ResizablePanel
